/**
 * Liff Context
 *
 * Line liff 的 context
 * 主要為判斷是否已用line登入，並獲取id token 做登入用
 * 與是否在Liff Browser內開啟
 *
 * 正式環境
 * 會Block非Liff Browser開啟網頁的使用者
 *
 * 測試環境
 * 開啟自動導向line login (endopint需配合ngrok之類的套件)
 *
 * API詳情 請參考 @https://developers.line.biz/en/reference/liff/#client-api
 */
import * as PropTypes from 'prop-types';
import {
  Consumer,
  Context,
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react';
import liff from '@line/liff';
import { Liff } from '@line/liff';
// import { useLocation } from 'react-router-dom';

// type Liff = Liff & Liff;

declare global {
  interface Window {
    liff?: Liff;
  }
}
interface LiffProviderProps<T> {
  liffId: string;
  search?: string;
  stubEnabled?: boolean | Partial<T>;
  children?:ReactNode;
}
interface LiffContext {
  error?: unknown;
  isLoggedIn: boolean;
  liff: Liff;
  ready: boolean;
  decodedIdToken?:
    | { picture?: string; name?: string; sub?: string }
    | null
    | undefined;
  idToken: string | null | undefined;
  accessToken: string | null | undefined;
  isInClient: boolean;
}
type CreateLiffContext = <T extends Liff>() => {
  LiffConsumer: Consumer<LiffContext>;
  LiffProvider: FC<LiffProviderProps<T>>;
  useLiff: () => LiffContext;
};

/**
 * LINE 開 liff.line.me/{id}/路徑?參數 時,會先把我們的路徑與 query 塞進 liff.state,
 * 等 liff.init() 完成才由 SDK 導到真正的網址。這段期間 window.location.search
 * 讀到的是 liff.state,不是我們的參數。
 */
export const hasPendingLiffStateRedirect = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).has('liff.state');
  } catch {
    return false;
  }
};

const initLiff = async <T extends Liff>({
  liffId,
  search,
}: LiffProviderProps<T>) => {
  
  try {
    await liff.init({ liffId });
    const searchParams = new URLSearchParams(search);
    
    if (searchParams.get('liff.state')) {
      console.log('wait for liff to redirect url');
      await new Promise((resolve) => {});
    }
    return { ready: true };
  } catch (error) {
    console.log(error);
    return { error, ready: false };
  }
};

const LiffProviderPropTypes = {
  children: PropTypes.element.isRequired,
  // liffId: PropTypes.string.isRequired,
};

const createLiffProvider = <T extends Liff>(context: Context<LiffContext>) => {
  const LiffProvider: FC<LiffProviderProps<T>> = ({ children, liffId }) => {
    const [error, setError] = useState<unknown>();
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [isInClient, setIsInClient] = useState<boolean>(false);
    const [ready, setReady] = useState(false);
    // liff.state 還沒被 SDK 換成真網址前不要 render 頁面。
    //
    // 不擋的話,子頁在第一次 render 就用「還是 liff.state 的網址」去讀 query,
    // 讀到的是空的;等 SDK 導完網址,那些用 useState initializer 只算一次的值不會重算,
    // 頁面就會停在錯的狀態。訪客歸還從 LINE 回來時會因此變成「請重新掃描包裝」,
    // 而且相機已被前一次卸載,畫面整片反白(同仁 2026-09-01 回報)。
    const [waitingLiffState, setWaitingLiffState] = useState<boolean>(
      hasPendingLiffStateRedirect
    );
    //  const { search } = useLocation();

    useEffect(() => {
      (async () => {

        if (liffId) {
          
          const { error, ready } = await initLiff({
            liffId,
            search: window.location.search
          });
          setError(error);
          // setLiff(liff as T);

          
          // 單一入口改版後：歸還入口頁 /rent 與掃描頁 /return/* 都不強制登入
          // 掃到包裝後，由 return.tsx 依 customer 欄位判斷是否需要呼叫 liff.login()
          // 在 LINE 內卻不是以 LIFF 身分載入 → 導成 LIFF 網址重開。
          //
          // 圖文選單指到 https://vip.packageplus.space/...(還先經 Omnichat 轉址),
          // 立牌 QR 也是印一般網址且已經發出去改不了。這種頁面在 LINE 內建瀏覽器
          // 屬於一般網頁,iOS 不會給相機權限 —— 使用者一路走到掃描就是 NotAllowedError。
          // 9/1 一天就有 66 次,而且 56 次的 UA 確實是 LINE。
          //
          // 這裡把它導成 liff.line.me/{id}/原路徑?原參數 重開一次,之後就是 LIFF 環境,
          // 相機權限跟著 LINE App 走。用 sessionStorage 記一次,避免導轉失敗時無限繞。
          const inLineApp = /\bLine\//i.test(navigator.userAgent);
          const REDIRECT_ONCE_KEY = 'pp_liff_entry_redirected';
          let alreadyTried = false;
          try {
            alreadyTried = window.sessionStorage.getItem(REDIRECT_ONCE_KEY) === '1';
          } catch {
            alreadyTried = false;
          }

          if (inLineApp && !liff.isInClient() && !alreadyTried) {
            try {
              window.sessionStorage.setItem(REDIRECT_ONCE_KEY, '1');
            } catch {
              /* 無痕模式寫不進去,大不了多繞一次 */
            }
            // 帶一個標記過去:之後看相機錯誤紀錄的 page_url 就知道「導轉到底有沒有發生」,
            // 有 liffr=1 卻還是拿不到相機 = 導轉有跑但沒進到 LIFF 環境;
            // 完全沒有 liffr=1 = 這段根本沒被觸發。不用再靠猜的。
            const sep = window.location.search ? '&' : '?';
            const target =
              `https://liff.line.me/${liffId}` +
              window.location.pathname +
              window.location.search + sep + 'liffr=1';
            window.location.replace(target);

            return;
          }

          const pathname = window.location.pathname;
          const skipAutoLogin =
            pathname.startsWith('/return') ||
            pathname === '/rent' ||
            pathname.startsWith('/rent/');

          if (!liff.isLoggedIn() && !skipAutoLogin) {
              liff.login({ redirectUri: window.location.href });
          }
          setIsInClient(liff.isInClient());
          setReady(ready);
          setIsLoggedIn(liff.isLoggedIn());
          setWaitingLiffState(false);
        } else {
          console.error("doesn't provide liff id");
        }
      })();
      // eslint-disable-next-line
    }, [liffId]);

    // 保險絲:萬一 LIFF 沒有如期導轉(SDK 失敗 / 網路異常),不能讓使用者卡在白畫面。
    useEffect(() => {
      if (!waitingLiffState) return;
      const t = setTimeout(() => setWaitingLiffState(false), 8000);

      return () => clearTimeout(t);
    }, [waitingLiffState]);
    
    return (
      <context.Provider
        value={{
          error,
          liff,
          decodedIdToken: isLoggedIn ? liff.getDecodedIDToken() : undefined,
          idToken: isLoggedIn ? liff.getIDToken() : undefined,
          accessToken: isLoggedIn ? liff.getAccessToken() : undefined,
          isInClient,
          isLoggedIn,
          ready,
        }}
      >
        {waitingLiffState ? null : children}
      </context.Provider>
    );
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  LiffProvider.propTypes = LiffProviderPropTypes;
  return LiffProvider;
};

export const createLiffContext: CreateLiffContext = () => {
  const context = createContext<LiffContext>({
    isLoggedIn: false,
    liff,
    decodedIdToken: undefined,
    idToken: undefined,
    accessToken: undefined,
    ready: false,
    isInClient: false,
  });
  context.displayName = 'LiffContext';

  return {
    LiffConsumer: context.Consumer,
    LiffProvider: createLiffProvider(context),
    useLiff: () => useContext(context),
  };
};

export const { LiffConsumer, LiffProvider, useLiff } =
  createLiffContext<Liff>();
